import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import toast from 'react-hot-toast';

// These URLs are constructed dynamically to load FFmpeg assets directly from node_modules.
const ffmpegCoreMtUrl = new URL(
  "../node_modules/@ffmpeg/core-mt/dist/umd/ffmpeg-core.js",
  import.meta.url
).href;
const ffmpegCoreMtWasmUrl = new URL(
  "../node_modules/@ffmpeg/core-mt/dist/umd/ffmpeg-core.wasm",
  import.meta.url
).href;
const ffmpegCoreMtWorkerUrl = new URL(
  "../node_modules/@ffmpeg/core-mt/dist/umd/ffmpeg-core.worker.js",
  import.meta.url
).href;

const DB_NAME = 'TdeMeetDB';
const DB_VERSION = 3;
const RECORDINGS_STORE_NAME = 'recordings';
const CHUNKS_STORE_NAME = 'recording_chunks';
const PENDING_RECORDINGS_STORE_NAME = 'pending_recordings';

export interface Recording {
  id: string;
  name: string;
  blob: Blob;
  timestamp: number;
  duration: number;
  roomId?: string;
  size: number;
}

export interface RecordingChunk {
    recordingId: number;
    chunk: Blob;
    order: number;
    timestamp: number;
}

export interface PendingRecording {
    id: number;
    name: string;
    roomId?: string;
    startTime: number;
    stopTime?: number;
    duration?: number;
}

let db: IDBDatabase;
// --- Singleton Pattern for FFmpeg ---
let ffmpeg: FFmpeg | null = null;
let ffmpegLoadingPromise: Promise<FFmpeg> | null = null;

/**
 * Gets the duration of a video blob by loading its metadata.
 */
function getBlobDuration(blob: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = function () {
            window.URL.revokeObjectURL(video.src);
            if (isFinite(video.duration)) {
                resolve(video.duration * 1000);
            } else {
                reject(new Error('Video duration is reported as non-finite.'));
            }
        };
        video.onerror = function () {
            window.URL.revokeObjectURL(video.src);
            reject(new Error('Failed to load video metadata to determine duration.'));
        };
        video.src = window.URL.createObjectURL(blob);
    });
}

/**
 * Loads a shared, single instance of FFmpeg.
 * If an instance is already loading, it waits for it to complete.
 */
async function loadFFmpeg(): Promise<FFmpeg> {
    if (ffmpeg && ffmpeg.loaded) {
        return ffmpeg;
    }
    if (ffmpegLoadingPromise) {
        return ffmpegLoadingPromise;
    }

    ffmpegLoadingPromise = (async () => {
        const newFFmpeg = new FFmpeg();
        newFFmpeg.on('log', ({ message }) => {
            console.log(`[FFmpeg]: ${message}`);
        });
        try {
            await newFFmpeg.load({
                coreURL: ffmpegCoreMtUrl,
                wasmURL: ffmpegCoreMtWasmUrl,
                workerURL: ffmpegCoreMtWorkerUrl
            });
            ffmpeg = newFFmpeg;
            return ffmpeg;
        } catch (e) {
            console.error("Failed to load FFmpeg", e);
            ffmpegLoadingPromise = null; // Reset promise on failure
            throw e;
        }
    })();
    return ffmpegLoadingPromise;
}

/**
 * Processes a video blob using the shared FFmpeg instance.
 */
async function processWithFFmpeg(blob: Blob): Promise<{ processedBlob: Blob, duration: number }> {
    const ffmpegInstance = await loadFFmpeg();
    const inputFileName = 'input.webm';
    const outputFileName = 'output.mp4';
    
    await ffmpegInstance.writeFile(inputFileName, await fetchFile(blob));

    // Hybrid approach: Copy video stream (fast), re-encode audio to AAC (for compatibility), and fix timestamps.
    await ffmpegInstance.exec(['-i', inputFileName, '-c:v', 'copy', '-c:a', 'aac', '-fflags', '+genpts', '-movflags', '+faststart', outputFileName]);
    
    const data = await ffmpegInstance.readFile(outputFileName);
    const processedBlob = new Blob([(data as Uint8Array).buffer], { type: 'video/mp4' });
    
    // Clean up files without terminating the shared instance
    await ffmpegInstance.deleteFile(inputFileName);
    await ffmpegInstance.deleteFile(outputFileName);

    const duration = await getBlobDuration(processedBlob);
    return { processedBlob, duration };
}


function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (db) return resolve(db);
    if (typeof window === 'undefined' || !window.indexedDB) {
        return reject(new Error('IndexedDB not supported.'));
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(new Error('Error opening IndexedDB'));
    request.onsuccess = (event) => {
      db = (event.target as IDBOpenDBRequest).result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(RECORDINGS_STORE_NAME)) {
        db.createObjectStore(RECORDINGS_STORE_NAME, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(CHUNKS_STORE_NAME)) {
        const chunkStore = db.createObjectStore(CHUNKS_STORE_NAME, { autoIncrement: true });
        chunkStore.createIndex('recordingId', 'recordingId', { unique: false });
      }
      if (!db.objectStoreNames.contains(PENDING_RECORDINGS_STORE_NAME)) {
        db.createObjectStore(PENDING_RECORDINGS_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

export async function saveRecording(recording: Omit<Recording, 'id'> & { id?: string }): Promise<void> {
    const db = await openDB();
    const transaction = db.transaction(RECORDINGS_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(RECORDINGS_STORE_NAME);
  
    const newRecording: Recording = {
        ...recording,
        id: recording.id || new Date().toISOString(),
    };
  
    store.put(newRecording);
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error('Error saving recording'));
    });
}
  
export async function getRecordings(): Promise<Recording[]> {
    const db = await openDB();
    const transaction = db.transaction(RECORDINGS_STORE_NAME, 'readonly');
    const store = transaction.objectStore(RECORDINGS_STORE_NAME);
    const request = store.getAll();
  
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
          resolve(request.result.sort((a, b) => b.timestamp - a.timestamp));
      };
      request.onerror = () => {
          reject(new Error('Error fetching recordings'));
      };
    });
}
  
export async function deleteRecording(id: string): Promise<void> {
      const db = await openDB();
      const transaction = db.transaction(RECORDINGS_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(RECORDINGS_STORE_NAME);
      const request = store.delete(id);
  
      return new Promise((resolve, reject) => {
          transaction.oncomplete = () => resolve();
          transaction.onerror = () => reject(new Error('Error deleting recording'));
      });
}
  
export async function clearAllRecordings(): Promise<void> {
      const db = await openDB();
      const transaction = db.transaction(RECORDINGS_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(RECORDINGS_STORE_NAME);
      store.clear();
      return new Promise((resolve, reject) => {
          transaction.oncomplete = () => resolve();
          transaction.onerror = () => reject(new Error('Error clearing recordings'));
      });
}
  
export async function saveRecordingChunk(chunkData: RecordingChunk): Promise<void> {
      const db = await openDB();
      const transaction = db.transaction(CHUNKS_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(CHUNKS_STORE_NAME);
      store.put(chunkData);
      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(new Error('Error saving recording chunk'));
      });
}

export async function startRecordingSession(pendingRecording: PendingRecording): Promise<void> {
    const db = await openDB();
    const transaction = db.transaction(PENDING_RECORDINGS_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(PENDING_RECORDINGS_STORE_NAME);
    store.put(pendingRecording);
    return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = (event) => reject(event);
    });
}

export async function stopRecordingSession(id: number, stopTime: number, duration: number): Promise<void> {
    const db = await openDB();
    const transaction = db.transaction(PENDING_RECORDINGS_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(PENDING_RECORDINGS_STORE_NAME);
    const request = store.get(id);

    return new Promise((resolve, reject) => {
        request.onerror = (event) => reject(event);
        request.onsuccess = () => {
            const data = request.result;
            if (data) {
                data.stopTime = stopTime;
                data.duration = duration;
                store.put(data);
                transaction.oncomplete = () => resolve();
            } else {
                reject(new Error(`Pending recording with id ${id} not found.`));
            }
        };
    });
}

export async function assembleAndSaveRecording(
    pendingRecordingId: number,
    inMemoryChunks?: Blob[]
): Promise<boolean> {
    const db = await openDB();
    
    const pending = await new Promise<PendingRecording | undefined>((resolve, reject) => {
        const tx = db.transaction(PENDING_RECORDINGS_STORE_NAME, 'readonly');
        const store = tx.objectStore(PENDING_RECORDINGS_STORE_NAME);
        const req = store.get(pendingRecordingId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = (e) => reject(e);
    });

    if (!pending) {
        console.error(`Pending recording with id ${pendingRecordingId} not found.`);
        return false;
    }

    let blobChunks: Blob[];
    if (inMemoryChunks && inMemoryChunks.length > 0) {
        blobChunks = inMemoryChunks;
    } else {
        const dbChunks = await new Promise<RecordingChunk[]>((resolve, reject) => {
            const tx = db.transaction(CHUNKS_STORE_NAME, 'readonly');
            const store = tx.objectStore(CHUNKS_STORE_NAME);
            const index = store.index('recordingId');
            const req = index.getAll(pending.id);
            req.onsuccess = () => resolve(req.result);
            req.onerror = (e) => reject(e);
        });
        blobChunks = dbChunks.sort((a, b) => a.order - b.order).map(c => c.chunk);
    }

    if (blobChunks.length === 0) {
        const cleanupTx = db.transaction(PENDING_RECORDINGS_STORE_NAME, 'readwrite');
        cleanupTx.objectStore(PENDING_RECORDINGS_STORE_NAME).delete(pending.id);
        console.warn(`No chunks for recording ${pending.id}, cleaning up.`);
        return false;
    }

    const fullBlob = new Blob(blobChunks, { type: 'video/webm' });
    
    let finalBlob: Blob;
    let duration: number;
    let recordingName = pending.name.replace(/\.webm$/, '.mp4');
    const isCrashRecovery = !pending.duration;
    
    const toastId = `ffmpeg-toast-${pending.id}`;
    try {
        toast.loading('Elaborazione registrazione in corso...', { id: toastId });
        const result = await processWithFFmpeg(fullBlob);
        finalBlob = result.processedBlob;
        duration = result.duration;
        toast.success('Registrazione elaborata con successo!', { id: toastId });

        if (isCrashRecovery) {
            recordingName = `[RECUPERATA] ${recordingName}`;
        }
    } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        console.error("FFmpeg processing failed. Saving raw blob as fallback.", error);
        toast.error(`Elaborazione fallita. Salvataggio del file originale.`, { id: toastId });
        
        finalBlob = fullBlob;
        duration = pending.duration || (Date.now() - pending.startTime); 
        recordingName = `[DA VERIFICARE] ${pending.name}`;
    }

    if (!isFinite(duration) || duration < 0) {
        duration = 0;
    }

    const newRecording: Recording = {
        id: pending.id.toString(),
        name: recordingName,
        blob: finalBlob,
        timestamp: pending.stopTime ?? Date.now(),
        duration,
        roomId: pending.roomId,
        size: finalBlob.size
    };

    return new Promise<boolean>((resolve, reject) => {
        const tx = db.transaction([RECORDINGS_STORE_NAME, PENDING_RECORDINGS_STORE_NAME, CHUNKS_STORE_NAME], 'readwrite');
        const recordingsStore = tx.objectStore(RECORDINGS_STORE_NAME);
        const pendingStore = tx.objectStore(PENDING_RECORDINGS_STORE_NAME);
        const chunksStore = tx.objectStore(CHUNKS_STORE_NAME);

        recordingsStore.put(newRecording);
        pendingStore.delete(pending.id);
        
        const chunksIndex = chunksStore.index('recordingId');
        const cursorRequest = chunksIndex.openKeyCursor(IDBKeyRange.only(pending.id));
        cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (cursor) {
                chunksStore.delete(cursor.primaryKey);
                cursor.continue();
            }
        };

        tx.oncomplete = () => resolve(true);
        tx.onerror = () => {
            console.error("Transaction failed during final save:", tx.error);
            reject(new Error('Salvataggio e pulizia falliti.'));
        };
    });
}

export async function processPendingRecordings(): Promise<number> {
    const db = await openDB();
    const allPendingRecordings = await new Promise<PendingRecording[]>((resolve, reject) => {
        const req = db.transaction(PENDING_RECORDINGS_STORE_NAME, 'readonly')
                      .objectStore(PENDING_RECORDINGS_STORE_NAME)
                      .getAll();
        req.onsuccess = () => resolve(req.result);
        req.onerror = (e) => reject(e);
    });

    if (allPendingRecordings.length === 0) {
        return 0;
    }
    
    let processedCount = 0;
    for (const pending of allPendingRecordings) {
        try {
            const success = await assembleAndSaveRecording(pending.id);
            if (success) {
                processedCount++;
            }
        } catch (error) {
            console.error(`Failed to process pending recording ${pending.id}:`, error);
        }
    }
    
    return processedCount;
}

